/**
 * 2ECOMP MODIFIED FILE - Contains customizations
 *
 * Customizations:
 * - PrintHeader: Custom header for PDF/print export
 * - printStyles.css: Print-specific styling
 * - nativePostMessage: PDF generation support
 * - printing state: Redux state for print mode
 * - onPrint: Print handler with data preloading
 * - hasResults: Track if report has data
 *
 * DO NOT overwrite with upstream without merging customizations.
 * Search for "2ECOMP" comments to find all customization points.
 */
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FormControl, InputLabel, Select, MenuItem, Table, TableHead, TableRow, TableBody, TableCell,
  Box, Chip, TextField, InputAdornment, Card, CardContent, Typography, Grid, Collapse,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import RouteIcon from '@mui/icons-material/Route';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SpeedIcon from '@mui/icons-material/Speed';
import TimerIcon from '@mui/icons-material/Timer';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TimelineIcon from '@mui/icons-material/Timeline';
import { StatCard, StatCardGrid } from '../common/components/premium/PremiumDashboard';
import { PremiumBarChart, PremiumAreaChart } from '../common/components/premium/PremiumCharts';
import {
  formatDistance, formatSpeed, formatVolume, formatTime, formatNumericHours,
} from '../common/util/formatter';
import ReportFilter, { updateReportParams } from './components/ReportFilter';
import { useAttributePreference } from '../common/util/preferences';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import usePersistedState from '../common/util/usePersistedState';
import ColumnSelect from './components/ColumnSelect';
import { useCatch } from '../reactHelper';
import useReportStyles from './common/useReportStyles';
import TableShimmer from '../common/components/TableShimmer';
import scheduleReport from './common/scheduleReport';
import fetchOrThrow from '../common/util/fetchOrThrow';
import exportExcel from '../common/util/exportExcel';
// 2ECOMP START: Print/PDF support
import { nativePostMessage } from '../common/components/NativeInterface';
import { sessionActions } from '../store';
import { PrintLayout } from './common/print';
// 2ECOMP END

const columnsArray = [
  ['startTime', 'reportStartDate'],
  ['distance', 'sharedDistance'],
  ['startOdometer', 'reportStartOdometer'],
  ['endOdometer', 'reportEndOdometer'],
  ['averageSpeed', 'reportAverageSpeed'],
  ['maxSpeed', 'reportMaximumSpeed'],
  ['engineHours', 'reportEngineHours'],
  ['startHours', 'reportStartEngineHours'],
  ['endHours', 'reportEndEngineHours'],
  ['spentFuel', 'reportSpentFuel'],
  ['fuelEfficiency', 'reportFuelEfficiency'],
  ['fuelCost', 'reportFuelCost'],
];
const columnsMap = new Map(columnsArray);

const SummaryReportPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { classes } = useReportStyles();
  const t = useTranslation();
  const theme = useTheme();
  const tokens = usePremiumTokens();

  const [searchParams, setSearchParams] = useSearchParams();

  const devices = useSelector((state) => state.devices.items, shallowEqual);
  const printing = useSelector((state) => state.session.printing ?? false);

  const distanceUnit = useAttributePreference('distanceUnit');
  const speedUnit = useAttributePreference('speedUnit');
  const volumeUnit = useAttributePreference('volumeUnit');

  const [columns, setColumns] = usePersistedState('summaryColumns', ['startTime', 'distance', 'averageSpeed']);
  const daily = searchParams.get('daily') === 'true';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // 2ECOMP START: Print/PDF state management
  const [hasResults, setHasResults] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [fuelPrice, setFuelPrice] = useState(5.50);
  const [showChart, setShowChart] = useState(true);
  // 2ECOMP END

  // Processar items para adicionar custo e eficiência de combustível
  const processedItems = useMemo(() => items.map((item) => {
    const device = devices[item.deviceId];
    let fuelUsed = item.spentFuel || 0;
    let fuelEstimated = false;

    // Se não há dados de combustível do sensor, estimar pelo consumo do dispositivo
    if (fuelUsed === 0 && device?.attributes?.form?.fuelConsumption) {
      const consumptionKmL = parseFloat(device.attributes.form.fuelConsumption);
      if (consumptionKmL > 0 && item.distance > 0) {
        fuelUsed = (item.distance / 1000) / consumptionKmL;
        fuelEstimated = true;
      }
    }

    // Calcular eficiência (km/L)
    const distanceKm = item.distance / 1000;
    const fuelEfficiency = fuelUsed > 0 ? distanceKm / fuelUsed : 0;

    return {
      ...item,
      fuelUsed,
      fuelEstimated,
      fuelEfficiency,
      fuelCost: fuelUsed * fuelPrice,
    };
  }), [items, devices, fuelPrice]);

  // Stats totais
  const stats = useMemo(() => {
    if (processedItems.length === 0) return null;
    const totalDistance = processedItems.reduce((acc, item) => acc + item.distance, 0);
    const totalFuel = processedItems.reduce((acc, item) => acc + item.fuelUsed, 0);
    const totalFuelCost = processedItems.reduce((acc, item) => acc + item.fuelCost, 0);
    const totalEngineHours = processedItems.reduce((acc, item) => acc + (item.engineHours || 0), 0);
    const avgSpeed = processedItems.reduce((acc, item) => acc + (item.averageSpeed || 0), 0) / processedItems.length;
    const avgEfficiency = totalFuel > 0 ? (totalDistance / 1000) / totalFuel : 0;

    return {
      vehicleCount: new Set(processedItems.map((i) => i.deviceId)).size,
      totalDistance: (totalDistance / 1000).toFixed(1),
      totalFuel: totalFuel.toFixed(2),
      totalFuelCost: totalFuelCost.toFixed(2),
      totalEngineHours: (totalEngineHours / 3600000).toFixed(1),
      avgSpeed: (avgSpeed * 1.852).toFixed(1),
      avgEfficiency: avgEfficiency.toFixed(2),
    };
  }, [processedItems]);

  // Dados para o gráfico (distância por veículo ou por dia)
  const chartData = useMemo(() => {
    if (processedItems.length === 0) return [];
    if (daily) {
      // Agregar por data
      const byDate = {};
      processedItems.forEach((item) => {
        const date = formatTime(item.startTime, 'date');
        if (!byDate[date]) {
          byDate[date] = { name: date, distance: 0, fuel: 0, hours: 0 };
        }
        byDate[date].distance += item.distance / 1000;
        byDate[date].fuel += item.fuelUsed;
        byDate[date].hours += (item.engineHours || 0) / 3600000;
      });
      return Object.values(byDate).slice(-14);
    }
    // Por veículo (top 10)
    const byVehicle = {};
    processedItems.forEach((item) => {
      const deviceName = devices[item.deviceId]?.name || `Device ${item.deviceId}`;
      if (!byVehicle[deviceName]) {
        byVehicle[deviceName] = { name: deviceName, distance: 0, fuel: 0, hours: 0 };
      }
      byVehicle[deviceName].distance += item.distance / 1000;
      byVehicle[deviceName].fuel += item.fuelUsed;
      byVehicle[deviceName].hours += (item.engineHours || 0) / 3600000;
    });
    return Object.values(byVehicle)
      .sort((a, b) => b.distance - a.distance)
      .slice(0, 10);
  }, [processedItems, daily, devices]);

  // 2ECOMP START: Print/PDF event handlers
  useEffect(() => {
    const handleAfterPrint = () => {
      dispatch(sessionActions.setPrinting(false));
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [dispatch]);

  useEffect(() => {
    if (printing) {
      const titleKey = daily ? 'reportDaily' : 'reportSummary';
      requestAnimationFrame(() =>
        requestAnimationFrame(() => nativePostMessage(`print|${t(titleKey)}.pdf`)),
      );
    }
  }, [printing, t, daily]);
  // 2ECOMP END

  const loadSummary = async ({ deviceIds, groupIds, from, to }) => {
    const query = new URLSearchParams({ from, to, daily });
    deviceIds.forEach((deviceId) => query.append('deviceId', deviceId));
    groupIds.forEach((groupId) => query.append('groupId', groupId));
    setLoading(true);
    try {
      const response = await fetchOrThrow(`/api/reports/summary?${query.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      const data = await response.json();
      setItems(data);
      setHasResults(data.length > 0);
    } finally {
      setLoading(false);
    }
  };

  const onShow = useCatch(async ({ deviceIds, groupIds, from, to }) => {
    setSelectedDeviceIds(deviceIds);
    setFromDate(from || '');
    setToDate(to || '');
    await loadSummary({ deviceIds, groupIds, from, to });
  });

  const getExportValue = (item, key) => formatValue(item, key) ?? '';

  const onExport = useCatch(async () => {
    if (!items.length) {
      return;
    }

    const sheets = new Map();
    items.forEach((item) => {
      const deviceName = devices[item.deviceId].name;
      if (!sheets.has(deviceName)) {
        sheets.set(deviceName, []);
      }
      const row = {};
      columns.forEach((key) => {
        const header = t(columnsMap.get(key));
        row[header] = getExportValue(item, key);
      });
      sheets.get(deviceName).push(row);
    });

    const titleKey = daily ? 'reportDaily' : 'reportSummary';
    await exportExcel(t(titleKey), `${t('reportSummary')}.xlsx`, sheets, theme);
  });

  const onSchedule = useCatch(async (deviceIds, groupIds, report) => {
    report.type = 'summary';
    report.attributes.daily = daily;
    await scheduleReport(deviceIds, groupIds, report);
    navigate('/reports/scheduled');
  });

  // 2ECOMP START: Print handler with data preloading
  const onPrint = useCatch(async ({ deviceIds = [], groupIds = [], from, to } = {}) => {
    const effectiveDeviceIds = deviceIds.length ? deviceIds : selectedDeviceIds;
    const effectiveFrom = from ?? fromDate;
    const effectiveTo = to ?? toDate;

    if (!effectiveDeviceIds.length || !effectiveFrom || !effectiveTo) {
      return;
    }

    if (!items.length) {
      await loadSummary({
        deviceIds: effectiveDeviceIds,
        groupIds,
        from: effectiveFrom,
        to: effectiveTo,
      });
    }

    setSelectedDeviceIds(effectiveDeviceIds);
    setFromDate(effectiveFrom);
    setToDate(effectiveTo);
    dispatch(sessionActions.setPrinting(true));
  });
  // 2ECOMP END

  const formatValue = (item, key) => {
    const value = item[key];
    switch (key) {
      case 'deviceId':
        return devices[value]?.name || value;
      case 'startTime':
        return formatTime(value, 'date');
      case 'startOdometer':
      case 'endOdometer':
      case 'distance':
        return formatDistance(value, distanceUnit, t);
      case 'averageSpeed':
      case 'maxSpeed':
        return value > 0 ? formatSpeed(value, speedUnit, t) : null;
      case 'engineHours':
      case 'startHours':
      case 'endHours':
        return value > 0 ? formatNumericHours(value, t) : null;
      case 'spentFuel':
        if (item.fuelUsed > 0) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {formatVolume(item.fuelUsed, volumeUnit, t)}
              {item.fuelEstimated && <Chip label="est" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />}
            </Box>
          );
        }
        return null;
      case 'fuelEfficiency':
        return item.fuelEfficiency > 0 ? `${item.fuelEfficiency.toFixed(2)} km/L` : null;
      case 'fuelCost':
        return item.fuelCost > 0 ? `R$ ${item.fuelCost.toFixed(2)}` : null;
      default:
        return value;
    }
  };

  const deviceNames = selectedDeviceIds
    .map((deviceId) => devices[deviceId]?.name || devices[deviceId]?.uniqueId || `${deviceId}`)
    .filter(Boolean)
    .join(', ');

  const titleKey = daily ? 'reportDaily' : 'reportSummary';

  // 2ECOMP START: Print/PDF render mode
  if (printing) {
    return (
      <PrintLayout
        title={t(titleKey)}
        from={fromDate}
        to={toDate}
        deviceName={deviceNames}
        onReady={() => window.print()}
      >
        <Table className="print-table">
          <TableHead>
            <TableRow>
              <TableCell>{t('sharedDevice')}</TableCell>
              {columns.map((key) => (<TableCell key={key}>{t(columnsMap.get(key))}</TableCell>))}
            </TableRow>
          </TableHead>
          <TableBody>
            {processedItems.map((item) => (
              <TableRow key={`print-${item.deviceId}-${item.startTime}`}>
                <TableCell>{devices[item.deviceId]?.name || item.deviceId}</TableCell>
                {columns.map((key) => (
                  <TableCell key={key}>
                    {formatValue(item, key)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </PrintLayout>
    );
  }
  // 2ECOMP END

  return (
    <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', titleKey]}>
      <div className={classes.header}>
        <ReportFilter
          onShow={onShow}
          onExport={onExport}
          onPrint={onPrint}
          onSchedule={onSchedule}
          deviceType="multiple"
          loading={loading}
          hasResults={hasResults}
          // Premium Export
          exportData={items}
          exportColumns={[
            { key: 'deviceId', label: t('sharedDevice'), render: (item) => devices[item.deviceId]?.name || item.deviceId },
            ...columns.map((key) => ({ key, label: t(columnsMap.get(key)) })),
          ]}
          exportTitle={t('reportSummary')}
          exportFilename="summary-report"
        >
          <div className={classes.filterItem}>
            <FormControl fullWidth>
              <InputLabel>{t('sharedType')}</InputLabel>
              <Select
                label={t('sharedType')}
                value={daily}
                onChange={(e) => updateReportParams(searchParams, setSearchParams, 'daily', [String(e.target.value)])}
              >
                <MenuItem value={false}>{t('reportSummary')}</MenuItem>
                <MenuItem value>{t('reportDaily')}</MenuItem>
              </Select>
            </FormControl>
          </div>
          <ColumnSelect columns={columns} setColumns={setColumns} columnsArray={columnsArray} />
          <div className={classes.filterItem}>
            <TextField
              size="small"
              type="number"
              label={t('reportFuelPrice')}
              value={fuelPrice}
              onChange={(e) => setFuelPrice(Number(e.target.value) || 0)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LocalGasStationIcon fontSize="small" /></InputAdornment>,
                endAdornment: <InputAdornment position="end">R$/L</InputAdornment>,
              }}
              inputProps={{ step: 0.01, min: 0 }}
              fullWidth
            />
          </div>
        </ReportFilter>
      </div>
      {/* Premium Stats Cards */}
      {stats && (
        <Box sx={{ px: 2, py: 2, borderTop: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}` }}>
          <StatCardGrid columns={6} gap={2}>
            <StatCard
              title={t('deviceTitle')}
              value={stats.vehicleCount}
              icon={<DirectionsCarIcon />}
              color="primary"
              size="sm"
            />
            <StatCard
              title={t('sharedDistance')}
              value={`${stats.totalDistance} km`}
              icon={<RouteIcon />}
              color="info"
              size="sm"
            />
            <StatCard
              title={t('reportSpentFuel')}
              value={`${stats.totalFuel} L`}
              icon={<LocalGasStationIcon />}
              color="warning"
              size="sm"
            />
            <StatCard
              title={t('reportFuelCost')}
              value={`R$ ${stats.totalFuelCost}`}
              icon={<AttachMoneyIcon />}
              color="success"
              size="sm"
            />
            <StatCard
              title={t('reportFuelEfficiency') || 'Eficiência'}
              value={`${stats.avgEfficiency} km/L`}
              icon={<TrendingUpIcon />}
              color="info"
              size="sm"
            />
            <StatCard
              title={t('reportEngineHours')}
              value={`${stats.totalEngineHours} h`}
              icon={<TimerIcon />}
              size="sm"
              subtitle={`${stats.avgSpeed} km/h ${t('sharedAverage')}`}
            />
          </StatCardGrid>

          {/* Chart Toggle */}
          {chartData.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box
                onClick={() => setShowChart(!showChart)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  color: tokens?.text?.secondary || '#8b949e',
                  '&:hover': { color: tokens?.accent?.primary || '#00d4aa' },
                }}
              >
                <TimelineIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t('reportChart') || 'Gráfico'}
                </Typography>
                {showChart ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </Box>
              <Collapse in={showChart}>
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: tokens?.surface?.elevated || '#161b22',
                    borderRadius: tokens?.radius?.lg || '12px',
                    border: `1px solid ${tokens?.border?.subtle || 'rgba(255,255,255,0.06)'}`,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 2, color: tokens?.text?.secondary || '#8b949e' }}
                  >
                    {t('sharedDistance')} {daily ? t('reportDaily') : t('deviceTitle')}
                  </Typography>
                  <PremiumBarChart
                    data={chartData}
                    xKey="name"
                    bars={[
                      { dataKey: 'distance', name: 'km', color: tokens?.accent?.primary || '#00d4aa' },
                    ]}
                    height={220}
                    layout={daily ? 'horizontal' : 'vertical'}
                    showLegend={false}
                    tooltipFormatter={(value) => `${value.toFixed(1)} km`}
                  />
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>
      )}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('sharedDevice')}</TableCell>
            {columns.map((key) => (<TableCell key={key}>{t(columnsMap.get(key))}</TableCell>))}
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading ? processedItems.map((item) => (
            <TableRow key={(`${item.deviceId}_${Date.parse(item.startTime)}`)}>
              <TableCell>{devices[item.deviceId]?.name}</TableCell>
              {columns.map((key) => (
                <TableCell key={key}>
                  {formatValue(item, key)}
                </TableCell>
              ))}
            </TableRow>
          )) : (<TableShimmer columns={columns.length + 1} />)}
        </TableBody>
      </Table>
    </PageLayout>
  );
};

export default SummaryReportPage;
